import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { fmtInvestment, fmtMoney } from "@/lib/api";
import { Users, ChevronDown } from "lucide-react";

export const RoleCard = ({ role, index, showPricing = false }) => {
  const [open, setOpen] = useState(false);
  const signals = role.signals || [];
  const why =
    signals.length > 0
      ? `Recommended because your assessment indicated: ${signals.join("; ")}.`
      : "Recommended because it complements your core operations based on your assessment responses.";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.07 }}
      className="rounded-xl border border-white/10 p-5"
      style={{ backgroundColor: "#0F1424" }}
      data-testid="role-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="rounded-lg bg-[#1E6BFF]/15 text-[#66B2FF] p-2">
            <Users size={18} />
          </span>
          <div>
            <h4 className="text-white font-semibold leading-tight">{role.name}</h4>
            <span className="text-xs text-white/50">{role.category}</span>
          </div>
        </div>
        <Badge className="bg-[#1E6BFF] text-white border-0 whitespace-nowrap">
          {role.quantity} {role.quantity > 1 ? "seats" : "seat"}
        </Badge>
      </div>

      <p className="text-white/65 text-sm mt-3 leading-relaxed">{role.summary}</p>

      <div className="flex flex-wrap gap-2 mt-4">
        {signals.map((sig, i) => (
          <span
            key={i}
            className="text-[11px] rounded-full px-2.5 py-1 bg-white/5 text-white/70 border border-white/10"
          >
            {sig}
          </span>
        ))}
      </div>

      {/* Why TeamBuilder AI recommended this role */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mt-4 w-full flex items-center justify-between text-left text-[#66B2FF] text-sm hover:text-white transition-colors"
        data-testid="role-why-toggle"
        aria-expanded={open}
      >
        <span>Why TeamBuilder AI recommended this role</span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="text-white/65 text-sm leading-relaxed mt-2 overflow-hidden"
            data-testid="role-why-content"
          >
            {why}
          </motion.p>
        )}
      </AnimatePresence>

      {showPricing && (
        <div className="mt-4 pt-4 border-t border-white/10 space-y-2.5" data-testid="role-pricing">
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/50">Estimated Monthly Investment</span>
            <span className="text-white font-semibold tabular-nums" data-testid="role-monthly-investment">
              {fmtInvestment(role.line_low, role.line_high)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/50">Estimated Annual Investment</span>
            <span className="text-white/80 font-medium tabular-nums" data-testid="role-annual-investment">
              {fmtInvestment(role.line_low * 12, role.line_high * 12, { suffix: "/year" })}
            </span>
          </div>
          {role.inhouse_line != null && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/50">Comparable In-House Cost</span>
              <span className="text-white/60 tabular-nums" data-testid="role-inhouse">
                {fmtMoney(role.inhouse_line)}/month
              </span>
            </div>
          )}
          {role.estimated_monthly_savings != null && role.estimated_monthly_savings > 0 && (
            <div className="flex items-center justify-between pt-1 border-t border-white/5">
              <span className="text-xs text-[#66B2FF]">Estimated Savings</span>
              <span className="text-[#66B2FF] font-semibold tabular-nums text-right" data-testid="role-savings">
                {fmtMoney(role.estimated_monthly_savings)}/mo
                <span className="block text-[11px] font-normal text-white/45">
                  {fmtMoney(role.estimated_annual_savings)}/year
                </span>
              </span>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default RoleCard;
